import { useState, useMemo } from 'react';
import {
  useMemberProjectRoles,
  useSetMemberProjectRole,
  useRemoveMemberProjectRole,
} from '../../hooks/useOrganizations';
import { useProjects } from '../../hooks/useProjects';
import { useCurrentOrganization } from '../../contexts/OrganizationContext';
import type { Member, OrganizationRole } from '../../types/organization';
import { ROLE_HIERARCHY } from '../../types/organization';
import { Badge, Button, EmptyState } from '../../ui';
import './CreateOrganizationModal.css';
import './ProjectRolesModal.css';

interface ProjectRolesModalProps {
  orgId: string;
  member: Member;
  onClose: () => void;
}

const ALL_ROLES: OrganizationRole[] = ['viewer', 'member', 'admin', 'owner'];
const ALL_SCOPE = 'all';

export function ProjectRolesModal({ orgId, member, onClose }: ProjectRolesModalProps) {
  const { currentRole } = useCurrentOrganization();
  const { data: assignments, isLoading } = useMemberProjectRoles(orgId, member.id);
  const { data: projects } = useProjects(orgId);
  const setProjectRole = useSetMemberProjectRole();
  const removeProjectRole = useRemoveMemberProjectRole();

  const [newProjectId, setNewProjectId] = useState<string>('');
  const [newRole, setNewRole] = useState<OrganizationRole>('member');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const availableRoles = useMemo<OrganizationRole[]>(() => {
    if (!currentRole) return [];
    return ALL_ROLES.filter(r => ROLE_HIERARCHY[r] <= ROLE_HIERARCHY[currentRole]);
  }, [currentRole]);

  const assignedProjectIds = useMemo(() => {
    return new Set((assignments ?? []).filter(a => a.scope === 'project').map(a => a.project_id));
  }, [assignments]);

  const assignableProjects = useMemo(() => {
    return (projects ?? []).filter(p => !assignedProjectIds.has(p.id));
  }, [projects, assignedProjectIds]);

  const hasAllProjectsRow = (assignments ?? []).some(a => a.scope === 'all_projects');

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    if (!newProjectId) return;
    try {
      await setProjectRole.mutateAsync({
        orgId,
        userId: member.id,
        scope: newProjectId,
        role: newRole,
      });
      setNewProjectId('');
      setNewRole('member');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to assign role');
    }
  };

  const handleRoleChange = async (scope: string, role: OrganizationRole) => {
    setErrorMessage(null);
    try {
      await setProjectRole.mutateAsync({ orgId, userId: member.id, scope, role });
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to update role');
    }
  };

  const handleRemove = async (scope: string) => {
    setErrorMessage(null);
    try {
      await removeProjectRole.mutateAsync({ orgId, userId: member.id, scope });
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to remove role');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <header className="modal__header">
          <h2>Project roles for {member.name}</h2>
          <button className="modal__close" onClick={onClose} type="button">
            &times;
          </button>
        </header>

        <div className="modal__body">
          <p className="role-description">
            Assign <strong>{member.name}</strong> a role scoped to a specific project, or to all
            current and future projects.
          </p>

          {errorMessage && <p className="form-error">{errorMessage}</p>}

          {isLoading ? (
            <div className="spinner" aria-label="Loading project roles" />
          ) : (assignments?.length ?? 0) === 0 ? (
            <EmptyState
              title="No project roles yet"
              description="Assign a role to a specific project or to all projects."
            />
          ) : (
            <ul className="project-roles-list" aria-label="Project role assignments">
              {assignments!.map(a => {
                const scopeKey = a.scope === 'all_projects' ? ALL_SCOPE : a.project_id!;
                return (
                  <li key={a.id} className="project-roles-list__row">
                    <div className="project-roles-list__label">
                      {a.scope === 'all_projects' ? (
                        <Badge tone="info">All projects</Badge>
                      ) : (
                        <span>{a.project_name || a.project_slug}</span>
                      )}
                    </div>
                    <select
                      value={a.role}
                      onChange={e => handleRoleChange(scopeKey, e.target.value as OrganizationRole)}
                      disabled={setProjectRole.isPending}
                      aria-label={`Role for ${a.scope === 'all_projects' ? 'all projects' : a.project_name}`}
                    >
                      {availableRoles.map(r => (
                        <option key={r} value={r}>
                          {r.charAt(0).toUpperCase() + r.slice(1)}
                        </option>
                      ))}
                    </select>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleRemove(scopeKey)}
                      disabled={removeProjectRole.isPending}
                    >
                      Remove
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}

          <form onSubmit={handleAdd} className="project-roles-form">
            <h3 className="project-roles-form__title">Add assignment</h3>
            <div className="form-group">
              <label htmlFor="project-scope">Project</label>
              <select
                id="project-scope"
                value={newProjectId}
                onChange={e => setNewProjectId(e.target.value)}
                required
              >
                <option value="">Select a project…</option>
                {!hasAllProjectsRow && <option value={ALL_SCOPE}>All projects</option>}
                {assignableProjects.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="project-role">Role</label>
              <select
                id="project-role"
                value={newRole}
                onChange={e => setNewRole(e.target.value as OrganizationRole)}
              >
                {availableRoles.map(r => (
                  <option key={r} value={r}>
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <Button type="submit" disabled={!newProjectId || setProjectRole.isPending}>
              {setProjectRole.isPending ? 'Assigning…' : 'Assign'}
            </Button>
          </form>
        </div>

        <footer className="modal__footer">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </footer>
      </div>
    </div>
  );
}
